import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import { UserRole } from "@/generated/types";
import { useInviteUser } from "../../../hooks/useInviteUser";
import { UserSearchCombobox } from "@/components/shared/UserSearchCombobox";
import type { SearchUserResult } from "@/hooks/useSearchUsers";

interface InviteUserDialogProps {
  onSuccess: () => void;
  children: React.ReactNode;
}

const ROLE_LABELS: Record<string, string> = {
  ADMIN: "Administrador",
  CLIENT: "Cliente",
  COLLABORATOR: "Colaborador",
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function InviteUserDialog({ onSuccess, children }: InviteUserDialogProps) {
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState<SearchUserResult | null>(null);
  const [role, setRole] = useState<string>(UserRole.Client);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const { inviteUser, loading } = useInviteUser({
    onCompleted: () => {
      toast.success("Convite enviado com sucesso.");
      setSearch("");
      setSelectedUser(null);
      setRole(UserRole.Client);
      setFormErrors({});
      onSuccess();
    },
  });

  const validate = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    if (selectedUser) {
      errors.search = "Este usuário já possui uma conta. Use a lista de usuários para gerenciá-lo.";
    } else if (!search.trim()) {
      errors.search = "Informe um e-mail para convidar";
    } else if (!EMAIL_PATTERN.test(search.trim())) {
      errors.search = "Por favor, insira um endereço de e-mail válido";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [search, selectedUser]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!validate()) return;
      inviteUser(search.trim(), role);
    },
    [validate, inviteUser, search, role]
  );

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setSearch("");
      setSelectedUser(null);
      setRole(UserRole.Client);
      setFormErrors({});
    }
  };

  return (
    <Dialog onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent maxWidth="max-w-lg">
        <DialogHeader>
          <DialogTitle>Convidar Usuário</DialogTitle>
          <DialogDescription>
            Pesquise usuários existentes ou informe um e-mail para enviar um convite de cadastro.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          <UserSearchCombobox
            value={search}
            onChange={(value) => {
              setSearch(value);
              setFormErrors((prev) => {
                const next = { ...prev };
                delete next.search;
                return next;
              });
            }}
            selectedUser={selectedUser}
            onSelectUser={(user) => {
              setSelectedUser(user);
              setFormErrors((prev) => {
                const next = { ...prev };
                delete next.search;
                return next;
              });
            }}
            label="E-mail ou nome"
            placeholder="Buscar usuário ou digitar e-mail"
            error={formErrors.search}
          />

          {selectedUser && (
            <Alert>
              <AlertDescription>
                {selectedUser.fullName} já está cadastrado. Novos convites por e-mail são apenas
                para pessoas que ainda não possuem conta.
              </AlertDescription>
            </Alert>
          )}

          {!selectedUser && search.trim().length >= 2 && (
            <p className="text-xs text-primary-600 dark:text-primary-400">
              Usuário não encontrado? O texto informado será usado como e-mail do convite.
            </p>
          )}

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-widest text-primary-600 dark:text-primary-400">
              Função
            </Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="rounded-none border-primary-200 dark:border-primary-800">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(UserRole).map((r) => (
                  <SelectItem key={r} value={r}>
                    {ROLE_LABELS[r] || r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button
                type="button"
                variant="outline"
                className="rounded-none uppercase tracking-widest text-xs border-primary-200 dark:border-primary-800"
              >
                Cancelar
              </Button>
            </DialogClose>
            <Button
              type="submit"
              disabled={loading || !!selectedUser}
              className="rounded-none uppercase tracking-widest text-xs"
            >
              {loading ? "Enviando..." : "Enviar Convite"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
